import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, AlertCircle, X } from 'lucide-react';
import { Button, Alert, AlertDescription } from '@/components/v2/ui';
import type { TextGenerateResponse } from '@/components/v2/tools/text/textGenerationApi';
import { useAIFieldApply } from './useAIFieldApply';
import { AIFieldPreviewActions } from './AIFieldPreviewActions';

export interface AIFieldPreviewProps {
  open: boolean;
  loading: boolean;
  result: TextGenerateResponse | null;
  error: string | null;
  productId: string;
  fieldType: string;
  channelId?: string;
  onClose: () => void;
}

/** Preview popover: shows generated text, then apply/undo write-back (Phase 3). */
export function AIFieldPreview({
  open,
  loading,
  result,
  error,
  productId,
  fieldType,
  channelId,
  onClose,
}: AIFieldPreviewProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const { apply, undo, reset, applying, applied, error: applyError } = useAIFieldApply();
  const handleCopy = async () => {
    if (!result?.content) return;
    await navigator.clipboard.writeText(result.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[70]" />
        <Dialog.Content className="fixed z-[70] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white dark:bg-neutral-900 rounded-xl shadow-xl p-6 focus:outline-none">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-base font-semibold text-neutral-900 dark:text-white">
              {t('aiField.previewTitle')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm"><X className="h-4 w-4" /></Button>
            </Dialog.Close>
          </div>

          {loading && (
            <div className="py-10 text-center">
              <Loader2 className="h-7 w-7 animate-spin text-primary-500 mx-auto mb-3" />
              <p className="text-sm text-neutral-500">{t('aiField.generating')}</p>
            </div>
          )}

          {(error || applyError) && !loading && (
            <Alert variant="error">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {applyError ? t('aiField.applyError') : t('aiField.error')}
              </AlertDescription>
            </Alert>
          )}

          {result && !loading && (
            <>
              <div className="max-h-72 overflow-y-auto whitespace-pre-wrap text-sm text-neutral-700 dark:text-neutral-300 bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3 border border-neutral-200 dark:border-neutral-700">
                {result.content}
              </div>
              <p className="mt-2 text-xs text-neutral-400">
                {t('aiField.creditsCharged', { count: result.credits_charged })}
              </p>
              <AIFieldPreviewActions
                applied={applied}
                applying={applying}
                copied={copied}
                onCopy={handleCopy}
                onApply={() => apply({ productId, fieldType, content: result.content, channelId })}
                onUndo={() => undo({ productId, fieldType, channelId })}
                onClose={handleClose}
              />
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
